import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, Stack, Chip, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Checkbox,
  CircularProgress, Tooltip, Divider, Tabs, Tab,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import ResendLog from '../admin/ResendLog';
import BulkReprocess from '../admin/BulkReprocess';
import { useAuth } from '../../contexts/AuthContext';

interface ConflictOption { id: string; value: string; }
interface Conflict { key: string; name: string; options: ConflictOption[]; }
interface PreviewReport {
  reportId: string;
  orderId: string | null;
  patientName: string | null;
  publishedAt: string | null;
  rowsChanged?: number;
  totalChanges?: number;
  namesReresolved?: number;
  duplicatesToRemove?: number;
  rowsWithoutReference?: number;
  conflicts?: Conflict[];
  hasConflict?: boolean;
  error?: string | null;
}
interface Totals { matched: number; clean: number; withConflict: number; errored: number; withChanges: number; }

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

const BulkResend: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { isAdmin } = useAuth();
  const [labs, setLabs] = useState<{ labName: string; count: number }[]>([]);
  const [labName, setLabName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [reports, setReports] = useState<PreviewReport[] | null>(null);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    api.get('/review/bulk-republish/labs')
      .then((r) => setLabs(r.data?.labs || []))
      .catch(() => setLabs([]));
  }, []);

  const isClean = (r: PreviewReport) => !r.error && !r.hasConflict;

  const runPreview = useCallback(async () => {
    if (!labName) { enqueueSnackbar('Pick a lab first', { variant: 'warning' }); return; }
    setLoading(true); setReports(null); setTotals(null); setSelected(new Set());
    try {
      const res = await api.post('/review/bulk-republish/preview', { labName, startDate: startDate || undefined, endDate: endDate || undefined });
      const rows: PreviewReport[] = res.data?.reports || [];
      setReports(rows);
      setTotals(res.data?.totals || null);
      // pre-select clean reports that actually have changes; conflicts/errors excluded
      setSelected(new Set(rows.filter((r) => isClean(r) && (r.totalChanges || 0) > 0).map((r) => r.reportId)));
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Preview failed', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [labName, startDate, endDate, enqueueSnackbar]);

  const toggle = (id: string) => {
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const selectableIds = useMemo(
    () => (reports || []).filter((r) => isClean(r) && (r.totalChanges || 0) > 0).map((r) => r.reportId),
    [reports],
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds));

  const apply = useCallback(async () => {
    if (selected.size === 0) return;
    setApplying(true);
    try {
      const items = Array.from(selected).map((reportId) => ({ reportId }));
      const res = await api.post('/review/bulk-republish/apply', {
        items, labName, startDate: startDate || undefined, endDate: endDate || undefined,
      });
      const s = res.data?.summary;
      enqueueSnackbar(`Re-sent ${s?.applied ?? 0} report(s)${s?.skipped ? `, ${s.skipped} skipped` : ''}${s?.failed ? `, ${s.failed} failed` : ''}`,
        { variant: s?.failed ? 'warning' : 'success' });
      await runPreview(); // refresh so applied reports drop to "no changes"
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Apply failed', { variant: 'error' });
    } finally {
      setApplying(false);
    }
  }, [selected, enqueueSnackbar, runPreview]);

  const changeSummary = (r: PreviewReport) => {
    if (r.error) return r.error;
    const parts: string[] = [];
    if (r.namesReresolved) parts.push(`${r.namesReresolved} name${r.namesReresolved > 1 ? 's' : ''} re-resolved`);
    const refFills = (r.totalChanges || 0) - (r.namesReresolved || 0);
    if (refFills > 0) parts.push(`${refFills} reference field${refFills > 1 ? 's' : ''} filled`);
    if (r.duplicatesToRemove) parts.push(`${r.duplicatesToRemove} duplicate${r.duplicatesToRemove > 1 ? 's' : ''} removed`);
    return parts.length ? parts.join(' · ') : 'No changes';
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>Bulk Fix &amp; Re-send</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Re-send" />
        <Tab label="Log" />
        {isAdmin && <Tab label="Reprocess" />}
      </Tabs>

      {tab === 1 && <ResendLog embedded />}
      {tab === 2 && isAdmin && <BulkReprocess embedded />}

      {tab === 0 && (
      <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Re-send already-published reports after a master correction. Reports with a value conflict are shown but
        <b> not</b> pushed — resolve those individually. The patient value is never changed.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
          <TextField
            select label="Lab" value={labName} onChange={(e) => setLabName(e.target.value)}
            sx={{ minWidth: 240 }} size="small"
          >
            {labs.length === 0 && <MenuItem value="" disabled>No published reports found</MenuItem>}
            {labs.map((l) => (
              <MenuItem key={l.labName} value={l.labName}>{l.labName} ({l.count})</MenuItem>
            ))}
          </TextField>
          <TextField label="From" type="date" size="small" value={startDate}
            onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="To" type="date" size="small" value={endDate}
            onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={runPreview} disabled={loading || !labName}>Preview</Button>
        </Stack>
      </Paper>

      {totals && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip label={`${totals.matched} matched`} />
          <Chip color="success" variant="outlined" label={`${totals.clean} clean`} />
          {totals.withConflict > 0 && <Chip color="warning" variant="outlined" label={`${totals.withConflict} conflict`} />}
          {totals.errored > 0 && <Chip color="error" variant="outlined" label={`${totals.errored} error`} />}
          <Box flexGrow={1} />
          <Chip color="primary" label={`${selected.size} selected`} />
        </Stack>
      )}

      {reports && reports.length === 0 && <Alert severity="info">No published reports match this lab / date range.</Alert>}

      {reports && reports.length > 0 && (
        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={allSelected}
                      indeterminate={!allSelected && selected.size > 0}
                      onChange={toggleAll} disabled={selectableIds.length === 0} />
                  </TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Published</TableCell>
                  <TableCell>Order ID</TableCell>
                  <TableCell>What will change</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((r) => {
                  const selectable = isClean(r) && (r.totalChanges || 0) > 0;
                  return (
                    <TableRow key={r.reportId} hover selected={selected.has(r.reportId)}>
                      <TableCell padding="checkbox">
                        <Tooltip title={
                          r.error ? 'Cannot re-send — ' + r.error
                            : r.hasConflict ? 'Has a value conflict — resolve this report individually'
                              : (r.totalChanges || 0) === 0 ? 'Nothing to change' : ''
                        }>
                          <span>
                            <Checkbox size="small" checked={selected.has(r.reportId)}
                              disabled={!selectable} onChange={() => toggle(r.reportId)} />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{r.patientName || '—'}</TableCell>
                      <TableCell>{fmtDate(r.publishedAt)}</TableCell>
                      <TableCell><code>{r.orderId || '—'}</code></TableCell>
                      <TableCell>
                        {changeSummary(r)}
                        {r.hasConflict && r.conflicts?.map((c) => (
                          <Box key={c.key} sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="warning.main">
                              ⚠ {c.name}: {c.options.map((o) => o.value).join(' vs ')}
                            </Typography>
                          </Box>
                        ))}
                      </TableCell>
                      <TableCell>
                        {r.error ? <Chip size="small" color="error" variant="outlined" label="Error" />
                          : r.hasConflict ? <Chip size="small" color="warning" label="Conflict" />
                            : (r.totalChanges || 0) === 0 ? <Chip size="small" variant="outlined" label="No change" />
                              : <Chip size="small" color="success" variant="outlined" label="Clean" />}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {selected.size} report(s) will be re-sent. Conflict / error rows are excluded.
            </Typography>
            <Box flexGrow={1} />
            <Button variant="contained" color="primary" startIcon={applying ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              disabled={applying || selected.size === 0} onClick={apply}>
              Done — Re-send {selected.size > 0 ? `(${selected.size})` : ''}
            </Button>
          </Box>
        </Paper>
      )}
      </Box>
      )}
    </Box>
  );
};

export default BulkResend;
